/**
 * HitFeedback — the 200ms after a hit lands.
 *
 * GAME-DESIGN §8: hitstop 60-90ms, screen shake, damage number, impact flash on
 * dealing; directional vignette and chromatic pulse on taking. This module owns
 * all of it so the timings live in one place and stay consistent between
 * abilities.
 *
 * ── What hitstop may and may not freeze ───────────────────────────────────
 *
 * Hitstop freezes PRESENTATION ONLY: interpolation, VFX and the camera. It must
 * never pause the simulation clock or the input sampler. The server keeps
 * ticking at 30Hz through your hitstop, and a client that stopped sampling input
 * for 80ms would send a gap the server fills with coast frames — you would
 * literally stop moving every time you landed a hit.
 */

import * as THREE from 'three/webgpu';
import { frame, localPlayer } from '../state.js';
import {
  triggerCameraShake,
  screenFlash,
  showDirectionalDamage,
  setLowHealthOverlay,
} from '../vfx/ScreenEffects.js';
import { playHitSound, playHurtSound, playKillSound, startHeartbeat } from '../audio/SoundManager.js';

/** Above this fraction of max health in one hit, everything scales up. */
const HEAVY_FRACTION = 0.22;
const LOW_HP_FRACTION = 0.3;

const NUMBER_LIFETIME_MS = 900;

let _camera = null;
let _cameraController = null;
let _layer = null;
const _pool = [];
const _live = [];
const _proj = new THREE.Vector3();

let _stopHeartbeat = null;
let _lowHp = false;

export function initHitFeedback(camera, cameraController) {
  _camera = camera;
  _cameraController = cameraController;
  _layer = document.getElementById('damage-numbers');
}

/**
 * Hitstop. `ms` is clamped to the 60-90ms design window: longer reads as a
 * frame hitch and, at 30Hz, starts eating a whole server tick of feel.
 */
export function hitstop(ms) {
  const clamped = Math.max(60, Math.min(90, ms));
  frame.hitstopUntil = Math.max(frame.hitstopUntil, performance.now() + clamped);
}

export function isHitstopped(now = performance.now()) {
  return now < frame.hitstopUntil;
}

/**
 * You hit someone.
 *
 * @param {{x:number,y:number,z:number}} point world position of the impact
 * @param {number} damage server-decided; the client never computes this
 */
export function onDealtDamage(point, damage, maxHealthOfTarget = 100) {
  const heavy = damage / maxHealthOfTarget >= HEAVY_FRACTION;

  hitstop(heavy ? 90 : 62);
  triggerCameraShake(heavy ? 0.42 : 0.16, heavy ? 220 : 110);
  screenFlash('#ffffff', 90, heavy ? 0.16 : 0.07);
  playHitSound(heavy);
  spawnDamageNumber(point, damage, heavy ? 'crit' : 'dealt');
}

/**
 * You took a hit.
 *
 * `fromPoint` is where it came from — the directional vignette is the only way
 * an artillery player is survivable, because it is the only cue that tells you
 * which way to turn.
 */
export function onTookDamage(damage, fromPoint, myPoint) {
  playHurtSound();
  triggerCameraShake(0.3, 180);
  _cameraController?.punchFov(2.5);

  if (fromPoint) {
    const dx = fromPoint.x - myPoint.x;
    const dz = fromPoint.z - myPoint.z;
    // Angle relative to where the camera is looking, so "left" means left on
    // screen and not left in world space. World atan2 angles grow CCW but the
    // vignette's screen bearing grows CW (0 = ahead, +π/2 = right), so the
    // difference must be NEGATED: `worldAngle - camYaw` kept front and back
    // correct while mirroring every side hit — the cue pointed AT the attacker's
    // mirror image, and players turned into the shot.
    const worldAngle = Math.atan2(dx, dz);
    const camYaw = Math.atan2(
      _camera.position.x - myPoint.x,
      _camera.position.z - myPoint.z
    );
    showDirectionalDamage(normalizeAngle(camYaw + Math.PI - worldAngle));
  } else {
    showDirectionalDamage(0, 0.35);
  }

  spawnDamageNumber({ x: myPoint.x, y: myPoint.y + 1.4, z: myPoint.z }, damage, 'taken');
}

/** Health changed for any reason, including the zone and the kill heal. */
export function onHealthChanged(health, maxHealth) {
  const low = health > 0 && health / maxHealth <= LOW_HP_FRACTION;
  if (low === _lowHp) return;
  _lowHp = low;
  setLowHealthOverlay(low);
  if (low) {
    _stopHeartbeat = startHeartbeat();
  } else {
    _stopHeartbeat?.();
    _stopHeartbeat = null;
  }
}

/** You got the kill. The heal and the ult chunk are the server's; this is the feel. */
export function onKillConfirmed() {
  playKillSound();
  screenFlash('#ffe98a', 260, 0.22);
  triggerCameraShake(0.5, 260);
  hitstop(90);
}

/** Clears anything that outlives a match. Called on the result screen. */
export function resetHitFeedback() {
  _stopHeartbeat?.();
  _stopHeartbeat = null;
  _lowHp = false;
  setLowHealthOverlay(false);
  for (const n of _live) {
    n.el.style.display = 'none';
    _pool.push(n.el);
  }
  _live.length = 0;
}

// ─── Damage numbers ─────────────────────────────────────────────

function acquire() {
  const el = _pool.pop();
  if (el) {
    el.style.display = '';
    return el;
  }
  const fresh = document.createElement('div');
  fresh.className = 'dmg';
  _layer.appendChild(fresh);
  return fresh;
}

/**
 * A floating number, projected from a world point.
 *
 * DOM rather than a sprite on purpose: text stays crisp at every resolution, it
 * costs no texture uploads, and the pool means eight simultaneous hits do not
 * allocate.
 */
export function spawnDamageNumber(point, damage, kind) {
  if (!_layer || !_camera) return;
  const el = acquire();
  el.className = `dmg ${kind}`;
  el.textContent = String(Math.max(1, Math.round(damage)));
  _live.push({
    el,
    world: new THREE.Vector3(point.x, point.y, point.z),
    born: performance.now(),
    drift: (Math.random() - 0.5) * 34,
  });
}

export function updateHitFeedback() {
  if (_live.length === 0) return;
  const now = performance.now();
  for (let i = _live.length - 1; i >= 0; i--) {
    const n = _live[i];
    const t = (now - n.born) / NUMBER_LIFETIME_MS;
    if (t >= 1) {
      n.el.style.display = 'none';
      _pool.push(n.el);
      _live.splice(i, 1);
      continue;
    }

    _proj.copy(n.world).project(_camera);
    // Behind the camera: hide rather than mirror it to the wrong side.
    if (_proj.z > 1) {
      n.el.style.opacity = '0';
      continue;
    }

    const x = (_proj.x * 0.5 + 0.5) * window.innerWidth + n.drift * t;
    const y = (-_proj.y * 0.5 + 0.5) * window.innerHeight - 46 * t;
    n.el.style.left = `${x}px`;
    n.el.style.top = `${y}px`;
    n.el.style.transform = `translate(-50%, -50%) scale(${1 + (1 - t) * 0.25})`;
    n.el.style.opacity = String(1 - t * t);
  }
}

function normalizeAngle(a) {
  const twoPi = Math.PI * 2;
  let x = a % twoPi;
  if (x > Math.PI) x -= twoPi;
  if (x < -Math.PI) x += twoPi;
  return x;
}

/** Convenience for callers that only have the local player's drawn position. */
export function localPoint(out) {
  return out.copy(localPlayer.renderPos);
}
